"use client";

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, FC, HTMLAttributes, ReactNode } from "react";
import React, { cloneElement, createContext, isValidElement, useCallback, use, useEffect, useState } from "react";
type PaginationPage = {
  /** The type of the pagination item. */
  type: "page";
  /** The value of the pagination item. */
  value: number;
  /** Whether the pagination item is the current page. */
  isCurrent: boolean;
};
type PaginationEllipsisType = {
  type: "ellipsis";
  key: number;
};
type PaginationItemType = PaginationPage | PaginationEllipsisType;
interface PaginationContextType {
  /** The pages of the pagination. */
  pages: PaginationItemType[];
  /** The current page of the pagination. */
  currentPage: number;
  /** The total number of pages. */
  total: number;
  /** The function to call when the page changes. */
  onPageChange: (page: number) => void;
}
const PaginationContext = createContext<PaginationContextType | undefined>(undefined);
export interface PaginationRootProps {
  /** Number of sibling pages to show on each side of the current page */
  siblingCount?: number;
  /** Current active page number */
  page: number;
  /** Total number of pages */
  total: number;
  children: ReactNode;
  /** The style of the pagination root. */
  style?: CSSProperties;
  /** The class name of the pagination root. */
  className?: string;
  /** Callback function that's called when the page changes with the new page number. */
  onPageChange?: (page: number) => void;
}
const PaginationRoot = t0 => {
  const $ = _c(22);
  const {
    total,
    siblingCount: t1,
    page,
    onPageChange,
    children,
    style,
    className
  } = t0;
  const siblingCount = t1 === undefined ? 1 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [pages, setPages] = useState(t2);
  let t3;
  if ($[1] !== page || $[2] !== siblingCount || $[3] !== total) {
    t3 = () => {
      const items = [];
      const totalPageNumbers = siblingCount * 2 + 5;
      if (totalPageNumbers >= total) {
        for (let i = 1; i <= total; i++) {
          items.push({
            type: "page",
            value: i,
            isCurrent: i === page
          });
        }
      } else {
        const leftSiblingIndex = Math.max(page - siblingCount, 1);
        const rightSiblingIndex = Math.min(page + siblingCount, total);
        const showLeftEllipsis = leftSiblingIndex > 2;
        const showRightEllipsis = rightSiblingIndex < total - 1;
        if (!showLeftEllipsis && showRightEllipsis) {
          const leftItemCount = siblingCount * 2 + 3;
          const leftRange = range(1, leftItemCount);
          leftRange.forEach(pageNum => items.push({
            type: "page",
            value: pageNum,
            isCurrent: pageNum === page
          }));
          items.push({
            type: "ellipsis",
            key: leftItemCount + 1
          });
          items.push({
            type: "page",
            value: total,
            isCurrent: total === page
          });
        } else {
          if (showLeftEllipsis && !showRightEllipsis) {
            const rightItemCount = siblingCount * 2 + 3;
            const rightRange = range(total - rightItemCount + 1, total);
            items.push({
              type: "page",
              value: 1,
              isCurrent: page === 1
            });
            items.push({
              type: "ellipsis",
              key: total - rightItemCount
            });
            rightRange.forEach(pageNum_0 => items.push({
              type: "page",
              value: pageNum_0,
              isCurrent: pageNum_0 === page
            }));
          } else {
            if (showLeftEllipsis && showRightEllipsis) {
              items.push({
                type: "page",
                value: 1,
                isCurrent: page === 1
              });
              items.push({
                type: "ellipsis",
                key: leftSiblingIndex - 1
              });
              const middleRange = range(leftSiblingIndex, rightSiblingIndex);
              middleRange.forEach(pageNum_1 => items.push({
                type: "page",
                value: pageNum_1,
                isCurrent: pageNum_1 === page
              }));
              items.push({
                type: "ellipsis",
                key: rightSiblingIndex + 1
              });
              items.push({
                type: "page",
                value: total,
                isCurrent: total === page
              });
            }
          }
        }
      }
      return items;
    };
    $[1] = page;
    $[2] = siblingCount;
    $[3] = total;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const createPaginationItems = t3;
  let t4;
  let t5;
  if ($[5] !== createPaginationItems) {
    t4 = () => {
      const paginationItems = createPaginationItems();
      setPages(paginationItems);
    };
    t5 = [createPaginationItems];
    $[5] = createPaginationItems;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== onPageChange) {
    t6 = newPage => {
      onPageChange?.(newPage);
    };
    $[8] = onPageChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onPageChangeHandler = t6;
  let t7;
  if ($[10] !== onPageChangeHandler || $[11] !== page || $[12] !== pages || $[13] !== total) {
    t7 = {
      pages,
      currentPage: page,
      total,
      onPageChange: onPageChangeHandler
    };
    $[10] = onPageChangeHandler;
    $[11] = page;
    $[12] = pages;
    $[13] = total;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const paginationContextValue = t7;
  let t8;
  if ($[15] !== children || $[16] !== className || $[17] !== style) {
    t8 = <nav aria-label="Pagination Navigation" style={style} className={className}>{children}</nav>;
    $[15] = children;
    $[16] = className;
    $[17] = style;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== paginationContextValue || $[20] !== t8) {
    t9 = <PaginationContext.Provider value={paginationContextValue}>{t8}</PaginationContext.Provider>;
    $[19] = paginationContextValue;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};

/**
 * Creates an array of numbers from start to end.
 * @param start - The start number.
 * @param end - The end number.
 * @returns An array of numbers from start to end.
 */
const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({
    length
  }, (_, index) => index + start);
};
interface TriggerRenderProps {
  isDisabled: boolean;
  onClick: () => void;
}
interface TriggerProps {
  /** The children of the trigger. Can be a render prop or a valid element. */
  children: ReactNode | ((props: TriggerRenderProps) => ReactNode);
  /** The style of the trigger. */
  style?: CSSProperties;
  /** The class name of the trigger. */
  className?: string | ((args: {
    isDisabled: boolean;
  }) => string);
  /** If true, the child element will be cloned and passed down the prop of the trigger. */
  asChild?: boolean;
  /** The direction of the trigger. */
  direction: "prev" | "next";
  /** The aria label of the trigger. */
  ariaLabel?: string;
}
const Trigger: FC<TriggerProps> = t0 => {
  const $ = _c(32);
  const {
    children,
    style,
    className,
    asChild: t1,
    direction,
    ariaLabel
  } = t0;
  const asChild = t1 === undefined ? false : t1;
  const context = use(PaginationContext);
  if (!context) {
    throw new Error("Pagination components must be used within a Pagination.Root");
  }
  const {
    currentPage,
    total,
    onPageChange
  } = context;
  const isDisabled = direction === "prev" ? currentPage <= 1 : currentPage >= total;
  let t2;
  if ($[0] !== currentPage || $[1] !== direction || $[2] !== isDisabled || $[3] !== onPageChange) {
    t2 = () => {
      if (isDisabled) {
        return;
      }
      const newPage = direction === "prev" ? currentPage - 1 : currentPage + 1;
      onPageChange?.(newPage);
    };
    $[0] = currentPage;
    $[1] = direction;
    $[2] = isDisabled;
    $[3] = onPageChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClick = t2;
  let t3;
  if ($[5] !== className || $[6] !== isDisabled) {
    t3 = typeof className === "function" ? className({
      isDisabled
    }) : className;
    $[5] = className;
    $[6] = isDisabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const computedClassName = t3;
  const defaultAriaLabel = direction === "prev" ? "Previous Page" : "Next Page";
  if (typeof children === "function") {
    let t4;
    if ($[8] !== children || $[9] !== handleClick || $[10] !== isDisabled) {
      t4 = children({
        isDisabled,
        onClick: handleClick
      });
      $[8] = children;
      $[9] = handleClick;
      $[10] = isDisabled;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== t4) {
      t5 = <>{t4}</>;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (asChild && isValidElement(children)) {
    const t4 = ariaLabel || defaultAriaLabel;
    const t5 = children.props as HTMLAttributes<HTMLElement>;
    let t6;
    if ($[14] !== children || $[15] !== computedClassName || $[16] !== handleClick || $[17] !== isDisabled || $[18] !== style || $[19] !== t4 || $[20] !== t5.style) {
      let t7;
      if ($[22] !== children.props || $[23] !== computedClassName) {
        t7 = [computedClassName, (children.props as HTMLAttributes<HTMLElement>).className].filter(Boolean).join(" ") || undefined;
        $[22] = children.props;
        $[23] = computedClassName;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      t6 = cloneElement(children, {
        onClick: handleClick,
        disabled: isDisabled,
        isDisabled,
        "aria-label": t4,
        style: {
          ...t5.style,
          ...style
        },
        className: t7
      } as HTMLAttributes<HTMLElement>);
      $[14] = children;
      $[15] = computedClassName;
      $[16] = handleClick;
      $[17] = isDisabled;
      $[18] = style;
      $[19] = t4;
      $[20] = t5.style;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    return t6;
  }
  const t4 = ariaLabel || defaultAriaLabel;
  let t5;
  if ($[25] !== children || $[26] !== computedClassName || $[27] !== handleClick || $[28] !== isDisabled || $[29] !== style || $[30] !== t4) {
    t5 = <button aria-label={t4} onClick={handleClick} disabled={isDisabled} style={style} className={computedClassName}>{children}</button>;
    $[25] = children;
    $[26] = computedClassName;
    $[27] = handleClick;
    $[28] = isDisabled;
    $[29] = style;
    $[30] = t4;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  return t5;
};
const PaginationPrevTrigger: FC<Omit<TriggerProps, "direction">> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Trigger {...props} direction="prev" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const PaginationNextTrigger: FC<Omit<TriggerProps, "direction">> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Trigger {...props} direction="next" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
interface PaginationItemRenderProps {
  isSelected: boolean;
  onClick: () => void;
  value: number;
  "aria-current"?: "page";
  "aria-label"?: string;
}
export interface PaginationItemProps {
  /** The value of the pagination item. */
  value: number;
  /** Whether the pagination item is the current page. */
  isCurrent: boolean;
  /** The children of the pagination item. Can be a render prop or a valid element. */
  children?: ReactNode | ((props: PaginationItemRenderProps) => ReactNode);
  /** The style object of the pagination item. */
  style?: CSSProperties;
  /** The class name of the pagination item. */
  className?: string | ((args: {
    isSelected: boolean;
  }) => string);
  /** The aria label of the pagination item. */
  ariaLabel?: string;
  /** If true, the child element will be cloned and passed down the prop of the item. */
  asChild?: boolean;
}
const PaginationItem = t0 => {
  const $ = _c(33);
  const {
    value,
    isCurrent,
    children,
    style,
    className,
    ariaLabel,
    asChild: t1
  } = t0;
  const asChild = t1 === undefined ? false : t1;
  const context = use(PaginationContext);
  if (!context) {
    throw new Error("Pagination components must be used within a <Pagination.Root />");
  }
  const {
    onPageChange
  } = context;
  const isSelected = isCurrent;
  let t2;
  if ($[0] !== onPageChange || $[1] !== value) {
    t2 = () => {
      onPageChange?.(value);
    };
    $[0] = onPageChange;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleClick = t2;
  let t3;
  if ($[3] !== className || $[4] !== isSelected) {
    t3 = typeof className === "function" ? className({
      isSelected
    }) : className;
    $[3] = className;
    $[4] = isSelected;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const computedClassName = t3;
  if (typeof children === "function") {
    const t4 = isCurrent ? "page" : undefined;
    const t5 = ariaLabel || `Page ${value}`;
    let t6;
    if ($[6] !== children || $[7] !== handleClick || $[8] !== isSelected || $[9] !== t4 || $[10] !== t5 || $[11] !== value) {
      t6 = children({
        isSelected,
        onClick: handleClick,
        value,
        "aria-current": t4,
        "aria-label": t5
      });
      $[6] = children;
      $[7] = handleClick;
      $[8] = isSelected;
      $[9] = t4;
      $[10] = t5;
      $[11] = value;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t6) {
      t7 = <>{t6}</>;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  if (asChild && isValidElement(children)) {
    const t4 = isCurrent ? "page" : undefined;
    const t5 = ariaLabel || `Page ${value}`;
    const t6 = children.props as HTMLAttributes<HTMLElement>;
    let t7;
    if ($[15] !== children || $[16] !== computedClassName || $[17] !== handleClick || $[18] !== style || $[19] !== t4 || $[20] !== t5 || $[21] !== t6.style) {
      let t8;
      if ($[23] !== children.props || $[24] !== computedClassName) {
        t8 = [computedClassName, (children.props as HTMLAttributes<HTMLElement>).className].filter(Boolean).join(" ") || undefined;
        $[23] = children.props;
        $[24] = computedClassName;
        $[25] = t8;
      } else {
        t8 = $[25];
      }
      t7 = cloneElement(children, {
        onClick: handleClick,
        "aria-current": t4,
        "aria-label": t5,
        style: {
          ...t6.style,
          ...style
        },
        className: t8
      } as HTMLAttributes<HTMLElement>);
      $[15] = children;
      $[16] = computedClassName;
      $[17] = handleClick;
      $[18] = style;
      $[19] = t4;
      $[20] = t5;
      $[21] = t6.style;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  const t4 = isCurrent ? "page" : undefined;
  const t5 = ariaLabel || `Page ${value}`;
  let t6;
  if ($[26] !== children || $[27] !== computedClassName || $[28] !== handleClick || $[29] !== style || $[30] !== t4 || $[31] !== t5) {
    t6 = <button onClick={handleClick} style={style} className={computedClassName} aria-current={t4} aria-label={t5} role="listitem">{children}</button>;
    $[26] = children;
    $[27] = computedClassName;
    $[28] = handleClick;
    $[29] = style;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
};
interface PaginationEllipsisProps {
  key: number;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string | (() => string);
}
const PaginationEllipsis: FC<PaginationEllipsisProps> = t0 => {
  const $ = _c(6);
  const {
    children,
    style,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = typeof className === "function" ? className() : className;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const computedClassName = t1;
  let t2;
  if ($[2] !== children || $[3] !== computedClassName || $[4] !== style) {
    t2 = <span style={style} className={computedClassName} aria-hidden="true">{children}</span>;
    $[2] = children;
    $[3] = computedClassName;
    $[4] = style;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
interface PaginationContextComponentProps {
  children: (pagination: PaginationContextType) => ReactNode;
}
const PaginationContextComponent: FC<PaginationContextComponentProps> = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  const context = use(PaginationContext);
  if (!context) {
    throw new Error("Pagination components must be used within a Pagination.Root");
  }
  let t1;
  if ($[0] !== children || $[1] !== context) {
    t1 = children(context);
    $[0] = children;
    $[1] = context;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <>{t1}</>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const Pagination = {
  Root: PaginationRoot,
  PrevTrigger: PaginationPrevTrigger,
  NextTrigger: PaginationNextTrigger,
  Item: PaginationItem,
  Ellipsis: PaginationEllipsis,
  Context: PaginationContextComponent
};